import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircleIcon, ChevronDownIcon, X } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '@/components/ui/modal';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { useSupportForm } from '@/hooks/useSupportForm';

/**
 * Stable reason *key* preselected when the Contact Support form is opened from
 * the Fellows Corner "Want to be a mentor?" CTA. Kept as a constant so the entry
 * point and the option list can't drift. This is the value submitted and matched
 * against, so it must stay stable even though the option's display label reads
 * as a friendlier first-person sentence.
 */
export const SUPPORT_REASON_BECOME_MENTOR = 'Become a Mentor';

/**
 * Stable reason key preselected when the form is opened from the advisor invitation
 * on an inquiry summary. Stable for the same reason as the mentor key: it is what the
 * support team matches on.
 */
export const SUPPORT_REASON_BECOME_LIBRARY_ADVISOR = 'Become an Inquiry Library Advisor';

/**
 * Stable reason key preselected when the form is opened from a thumbs-down on an inquiry
 * summary, so feedback on generated summaries arrives separated from other requests.
 */
export const SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK = 'Inquiry Summary Feedback';

/**
 * Stable reason key preselected when the form is opened from the time away sheet by an
 * expert who needs longer than the 30 day limit, so those requests arrive separated.
 */
export const SUPPORT_REASON_AWAY_OVER_30_DAYS = 'Time Away Over 30 Days';

const SUPPORT_REASONS = {
  'Sign In': 'Sign In',
  'Account Access': 'Account Access',
  'Sign Up': 'Sign Up',
  'Email Updates': 'Email Updates',
  'Expert Recommendations': 'Expert Recommendations',
  [SUPPORT_REASON_BECOME_MENTOR]: 'I’d like to become a mentor at Primum',
  [SUPPORT_REASON_BECOME_LIBRARY_ADVISOR]: 'I’d like to advise on the Inquiry Library',
  [SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK]: 'Feedback on an inquiry summary',
  [SUPPORT_REASON_AWAY_OVER_30_DAYS]: 'I need more than 30 days away',
  'Something Else': 'Something Else',
} as const;

/**
 * Looks up a reason's display label. The Select shows its raw value for a reason that
 * was preselected rather than picked, so an entry point that opens the form with a
 * reason chosen would otherwise show the stable key instead of the sentence.
 */
const REASON_LABELS: Record<string, string> = SUPPORT_REASONS;

const supportFormSchema = z.object({
  name: z.string().optional(),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  reason: z.string().min(1, 'Please select a reason'),
  message: z.string().min(1, 'Message is required'),
});

export type SupportFormData = z.infer<typeof supportFormSchema>;

export function SupportForm({
  isOpen,
  onClose,
  npi,
  institution,
  state,
  firstName,
  lastName,
  identifiesAsDoctor,
  initialValues = {},
  onSubmitted,
}: {
  isOpen: boolean;
  onClose: () => void;
  npi?: string;
  institution?: string;
  state?: string;
  firstName?: string;
  lastName?: string;
  identifiesAsDoctor?: boolean;
  initialValues?: Partial<SupportFormData>;
  /** Called once the message has been sent, before the form closes. Not called on failure. */
  onSubmitted?: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { colors } = useColors();

  const insets = useSafeAreaInsets();

  const supportForm = useSupportForm({
    initialValues,
    userData: {
      npi: npi ?? '',
      institution: institution ?? '',
      state: state ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      identifiesAsDoctor: Boolean(identifiesAsDoctor),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SupportFormData>({
    defaultValues: {
      name: initialValues.name || '',
      email: initialValues.email || '',
      reason: initialValues.reason || '',
      message: initialValues.message || '',
    },
    resolver: zodResolver(supportFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      setIsSubmitting(true);

      await supportForm.onSubmit(data);

      onSubmitted?.();

      reset();

      onClose();
    } catch (error) {
      console.error('Support Form ERROR:', error);
    } finally {
      setIsSubmitting(false);
    }
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      {/* The modal root is `web:pointer-events-none`; re-enable them on the
          backdrop and wire its press so clicking outside closes the form. */}
      <ModalBackdrop onPress={onClose} className="web:pointer-events-auto" />
      <ModalContent
        className="w-[90%] max-w-[500px] self-center"
        style={{
          marginTop: insets.top || 20,
          marginBottom: insets.bottom || 20,
          marginHorizontal: 0,
        }}>
        <ModalHeader>
          <Heading size="h2-xl">Contact Support</Heading>
          <ModalCloseButton onPress={onClose}>
            <Icon as={X} size="md" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <VStack space="md">
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.name?.message}>
                  <FormControlLabel className="mb-1">
                    <FormControlLabelText size="sm">Name</FormControlLabelText>
                  </FormControlLabel>

                  <Input size="sm">
                    <InputField
                      className="text-base text-typography-900"
                      value={value}
                      onChangeText={onChange}
                      autoCapitalize="words"
                    />
                  </Input>

                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{errors.name?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.email?.message}>
                  <FormControlLabel className="mb-1">
                    <FormControlLabelText size="sm">Email</FormControlLabelText>
                  </FormControlLabel>

                  <Input size="sm">
                    <InputField
                      className="text-base text-typography-900"
                      value={value}
                      onChangeText={onChange}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </Input>

                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{errors.email?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="reason"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.reason?.message}>
                  <FormControlLabel className="mb-1">
                    <FormControlLabelText size="sm">
                      What can we help you with?
                    </FormControlLabelText>
                  </FormControlLabel>

                  <Select
                    selectedValue={value || ''}
                    selectedLabel={REASON_LABELS[value] ?? value}
                    initialLabel={REASON_LABELS[value] ?? value}
                    onValueChange={onChange}>
                    <SelectTrigger className="justify-between">
                      <SelectInput
                        style={{ color: colors.typography[900] }}
                        placeholder="Select option"
                        className="text-base text-typography-900"
                      />
                      <SelectIcon className="mr-3" as={ChevronDownIcon} />
                    </SelectTrigger>

                    <SelectPortal>
                      <SelectBackdrop />
                      <SelectContent
                        className="max-h-[70%]"
                        style={{
                          paddingBottom: insets.bottom,
                        }}>
                        <SelectDragIndicatorWrapper>
                          <SelectDragIndicator />
                        </SelectDragIndicatorWrapper>
                        <SelectScrollView>
                          {Object.entries(SUPPORT_REASONS).map(([key, label]) => (
                            <SelectItem
                              key={key}
                              label={label}
                              value={key}
                              className="text-typography-900"
                            />
                          ))}
                        </SelectScrollView>
                      </SelectContent>
                    </SelectPortal>
                  </Select>

                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{errors.reason?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="message"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.message?.message}>
                  <FormControlLabel className="mb-1">
                    <FormControlLabelText size="sm">Message</FormControlLabelText>
                  </FormControlLabel>

                  <Textarea>
                    <TextareaInput
                      className="text-typography-900"
                      value={value}
                      onChangeText={onChange}
                      placeholder="Share any additional details for us to better help you."
                    />
                  </Textarea>

                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{errors.message?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
          </VStack>
        </ModalBody>

        <ModalFooter className="pt-4">
          <Button
            size="sm"
            variant="solid"
            onPress={handleFormSubmit}
            disabled={isSubmitting}
            className="w-full">
            {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText>Submit</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
